// @gs/feedback/client — FeedbackPage : orchestration liste <-> fil + modal de
// creation. Capability-aware (pattern Console UG V2) : 503 MIGRATION_116_PENDING
// -> bandeau explicite ; 404 (route non montee) -> rien d'affiche, AUCUN 500.
import { useState } from 'react';
import { useTranslation } from '@gs/i18n';
import { useFeedbackTickets, isFeedbackMigrationPending, isFeedbackUnavailable } from './hooks';
import { FeedbackModal } from './FeedbackModal';
import { TicketList } from './TicketList';
import { TicketThread } from './TicketThread';

export interface FeedbackPageProps {
  /** `false` tant que l'utilisateur n'est pas authentifie (regle enabled: !!user). */
  enabled: boolean;
  /**
   * Deep-link (cloche ntf, CHORE-NTF-BELL-LU) : ouvre ce ticket a l'arrivee et a
   * chaque changement. Le package ne connait PAS le routeur de l'app — l'app
   * fournit l'id (ex: query param `?ticket=`) et reflete la selection via
   * onOpenTicketChange (URL partageable, re-clic sur la meme notification re-ouvre).
   */
  openTicketId?: string | null;
  onOpenTicketChange?: (id: string | null) => void;
}

export function FeedbackPage({ enabled, openTicketId, onOpenTicketChange }: FeedbackPageProps) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(openTicketId ?? null);
  const [composing, setComposing] = useState(false);
  const tickets = useFeedbackTickets(enabled);

  const select = (id: string | null) => {
    setSelectedId(id);
    onOpenTicketChange?.(id);
  };
  // Sync prop -> etat PENDANT le rendu (pattern React « adjust state during
  // render » — un setState synchrone dans un useEffect est rejete par le React
  // Compiler) : navigation cloche pendant que la page est deja montee.
  const [prevOpenTicketId, setPrevOpenTicketId] = useState(openTicketId ?? null);
  if ((openTicketId ?? null) !== prevOpenTicketId) {
    setPrevOpenTicketId(openTicketId ?? null);
    if (openTicketId) setSelectedId(openTicketId);
  }

  if (tickets.isError) {
    if (isFeedbackUnavailable(tickets.error)) return null; // capability absente — aucun controle mort
    if (isFeedbackMigrationPending(tickets.error)) {
      return (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-meta text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300"
          role="status">
          {t('feedback:list.migrationPending')}
        </div>
      );
    }
    return (
      <div className="rounded-xl bg-white px-4 py-6 text-center text-meta text-slate-500 shadow-sm dark:bg-slate-900 dark:text-slate-400">
        {t('feedback:list.loadError')}
      </div>
    );
  }

  if (selectedId) {
    return <TicketThread ticketId={selectedId} onBack={() => select(null)} />;
  }

  return (
    <>
      {tickets.data ? (
        <TicketList
          tickets={tickets.data.tickets}
          staff={tickets.data.staff}
          onOpen={select}
          onNew={() => setComposing(true)}
        />
      ) : (
        <div className="h-40 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800" aria-busy="true" />
      )}
      <FeedbackModal
        open={composing}
        onClose={() => setComposing(false)}
        onCreated={(created) => select(created.id)}
      />
    </>
  );
}
